import { useMemo, useState } from "react";
import { Input } from "@progress/kendo-react-inputs";
import { Clock, XIcon, SearchIcon } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import ActionButton from "../pages/workspace/components/atoms/actionButton";
import CommercialHistoryItem from "./components/commercialHistoryItem";
import { clearConversation, clearCurrentCommercialPlan, selectConversation } from "../slices/chatSlice";
import { useCommercialHistoryConversations } from "./hooks/useCommercialHistoryConversations";
import { filterConversations, type CommercialHistoryItemStatusType } from "./hooks/useCommercialHistoryItem";
import { CommercialHistoryItemStatusTypes } from "../pages/workspace/components/types/commercialHistoryTypes";
import {
    commercialHistoryPeriodLabels,
    commercialHistoryPeriodOrder,
    groupConversationsByPeriod
} from "./utils/commercialHistoryList";

type CommercialHistoryPanelProps = {
    isOpen: boolean;
    onClose: () => void;
    onChange?: (conversationId: number) => void;
};

const CommercialHistoryPanel = ({ isOpen, onClose, onChange }: CommercialHistoryPanelProps) => {
    const dispatch = useAppDispatch();
    const chatConversation = useAppSelector(selectConversation);

    const { conversations, isLoading, isError } = useCommercialHistoryConversations();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredConversations = useMemo(
        () => filterConversations(conversations, searchTerm),
        [conversations, searchTerm]
    );

    const groupedConversations = useMemo(
        () => groupConversationsByPeriod(filteredConversations),
        [filteredConversations]
    );

    const handleSelectConversation = (conversationId: number, status: CommercialHistoryItemStatusType): void => {
        dispatch(clearConversation());

        if (status === CommercialHistoryItemStatusTypes.OPEN_CONVERSATION) {
            dispatch(clearCurrentCommercialPlan());
        }

        onChange?.(conversationId);
    };

    return (
        <div
            id="commercial-history-panel"
            className="commercial-history-panel flex-shrink-0 overflow-hidden h-100"
            style={{
                width: isOpen ? 300 : 0,
                transition: "width 250ms ease"
            }}
            aria-hidden={!isOpen}
        >
            <div className="commercial-history-panel-inner bg-white shadow-sm h-100 d-flex flex-column border-bottom">
                <div className="flex-shrink-0 p-3 k-border-brand-base">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2 min-w-0">
                            <Clock size={14} strokeWidth={2} className="k-text-primary" />
                            <h2 className="commercial-history-panel-title mb-0 text-truncate">Commercial History</h2>
                        </div>
                        <ActionButton
                            type="button"
                            variant="icon"
                            innerClassName="flex-shrink-0"
                            aria-label="Close commercial history"
                            endIcon={<XIcon size={14} strokeWidth={2} />}
                            onClick={onClose}
                        />
                    </div>
                    <p className="commercial-history-panel-subtitle mb-0">Previous simulations and conversations</p>
                    <div className="commercial-history-panel-search position-relative">
                        <span
                            className="commercial-history-panel-search-icon position-absolute top-50 translate-middle-y d-inline-flex align-items-center"
                            aria-hidden="true"
                        >
                            <SearchIcon size={14} strokeWidth={2} />
                        </span>
                        <Input
                            value={searchTerm}
                            className="commercial-history-panel-search-input w-100 rounded-3 !k-bg-brand-light-pink"
                            placeholder="Search history..."
                            aria-label="Search commercial history"
                            onChange={event => setSearchTerm((event.value as string) ?? "")}
                        />
                    </div>
                </div>

                <div className="flex-grow-1 overflow-auto px-3 py-3 min-h-0">
                    {isLoading && <p className="mb-0 small text-muted">Loading conversations...</p>}
                    {!isLoading && isError && <p className="mb-0 small text-danger">Unable to load conversations.</p>}
                    {!isLoading && !isError && filteredConversations.length === 0 && (
                        <p className="mb-0 small text-muted">No conversations yet.</p>
                    )}
                    {!isLoading && !isError && filteredConversations.length > 0 && (
                        <div className="d-flex flex-column gap-3 min-w-0">
                            {commercialHistoryPeriodOrder.map(period => {
                                const periodConversations = groupedConversations[period];

                                if (!periodConversations?.length) {
                                    return null;
                                }

                                return (
                                    <div key={period}>
                                        <p className="commercial-history-section-label small fw-semibold text-muted text-uppercase mb-2">
                                            {commercialHistoryPeriodLabels[period]}
                                        </p>
                                        <div className="d-flex flex-column gap-2 min-w-0">
                                            {periodConversations.map(conversation => (
                                                <CommercialHistoryItem
                                                    key={conversation.id}
                                                    conversation={conversation}
                                                    isSelected={chatConversation?.id === conversation.id}
                                                    onSelect={handleSelectConversation}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommercialHistoryPanel;
